/*
 * RCO pre-orders for shared-supply listings (carabiner badge reels).
 *
 * Reads the live status from the RCO ops app (supply-preorders), then:
 *  - fills any "RCO pre-order status" section on the page,
 *  - turns "Add to cart" into "Pre-order" and tags the cart line with the ship window,
 *  - shows a popup when a pre-order item is added,
 *  - shows a banner in the cart drawer and cart page while the cart holds one.
 * The ops app decides which orders are held; this script only explains it to the customer.
 */
(function () {
  if (window.RcoPreorder) return;

  var config = window.RcoPreorderConfig || {};
  var ENDPOINT = config.endpoint || 'https://rdfolimwjicgfujbmtig.supabase.co/functions/v1/supply-preorders';
  var SUPPLIES = config.supplies || ['carabiner'];
  var CACHE_MS = 60 * 1000;
  var PROPERTY = 'Pre-order';

  var statuses = {};
  var ready = null;

  function cacheKey(supply) { return 'rco-preorder-' + supply; }

  function loadStatus(supply) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey(supply)) || 'null');
      if (cached && Date.now() - cached.at < CACHE_MS) return Promise.resolve(cached.status);
    } catch (e) { /* storage unavailable */ }

    return fetch(ENDPOINT + '?supply=' + encodeURIComponent(supply))
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var status = body && body.status ? body.status : null;
        try { sessionStorage.setItem(cacheKey(supply), JSON.stringify({ at: Date.now(), status: status })); } catch (e) { /* ignore */ }
        return status;
      })
      .catch(function () { return null; });
  }

  function loadAll() {
    if (!ready) {
      ready = Promise.all(SUPPLIES.map(loadStatus)).then(function (results) {
        results.forEach(function (status, i) { if (status) statuses[SUPPLIES[i]] = status; });
        return statuses;
      });
    }
    return ready;
  }

  // The status for a variant when it is currently a pre-order, else null.
  function preorderFor(variantId) {
    var id = String(variantId || '');
    if (!id) return null;
    for (var key in statuses) {
      var status = statuses[key];
      var units = status && status.variants ? status.variants[id] : null;
      if (!units) continue;
      if (Number(status.in_stock_units) >= units) return null;
      if (Number(status.preorder_units) >= units && status.ship_label) return status;
      return null;
    }
    return null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  var SUPPLY_NAMES = { carabiner: 'Carabiners' };

  function shipText(status) { return 'Estimated to ship ' + status.ship_label; }

  // "Carabiners on backorder - estimated to ship Oct 21–27"
  function backorderLine(status) {
    var name = SUPPLY_NAMES[status.hardware_type] || 'This item';
    // Non-breaking space keeps "Oct 21–27" on one line.
    return name + ' on backorder - estimated to ship ' + String(status.ship_label).replace(' ', '\u00a0');
  }

  /* ---------- Theme pieces ---------- */

  var SVG_NS = 'http://www.w3.org/2000/svg';

  // The theme's ellipse icon, as used in its stock pills.
  function dot() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'rco-preorder-dot');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    var inner = document.createElementNS(SVG_NS, 'circle');
    inner.setAttribute('fill', 'currentColor');
    inner.setAttribute('cx', '8'); inner.setAttribute('cy', '8'); inner.setAttribute('r', '5');
    var ring = document.createElementNS(SVG_NS, 'circle');
    ring.setAttribute('cx', '8'); ring.setAttribute('cy', '8'); ring.setAttribute('r', '6.5');
    ring.setAttribute('stroke-opacity', '0.3'); ring.setAttribute('stroke-width', '3');
    svg.appendChild(inner);
    svg.appendChild(ring);
    return svg;
  }

  // Pill in the same shape as the theme's stock pill, in Rescue Candy orange.
  function statusPill(status) {
    var pill = el('div', 'rco-preorder-pill');
    pill.appendChild(dot());
    pill.appendChild(el('span', '', backorderLine(status)));
    return pill;
  }

  function icon(paths) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    paths.forEach(function (d) {
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      svg.appendChild(path);
    });
    return svg;
  }

  // Delivery truck and info-circle outlines.
  var TRUCK = ['M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2', 'M15 18H9', 'M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14', 'M7 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4z', 'M17 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4z'];
  var INFO = ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 16v-4', 'M12 8h.01'];

  // Order today -> restock arrives -> estimated to ship
  function stepsList(status) {
    var steps = el('ol', 'rco-preorder__steps');
    [
      ['Pre-order', 'Today', 'is-current'],
      ['Restock arrives', status.restock_label, ''],
      ['Estimated to ship', status.ship_label, 'is-highlight'],
    ].forEach(function (step) {
      var item = el('li', 'rco-preorder__step' + (step[2] ? ' ' + step[2] : ''));
      item.appendChild(el('span', 'rco-preorder__step-label', step[0]));
      item.appendChild(el('span', 'rco-preorder__step-value', step[1]));
      steps.appendChild(item);
    });
    return steps;
  }

  function ticker(text) {
    var track = el('div', 'rco-preorder__ticker-track');
    for (var i = 0; i < 2; i++) {
      var group = el('div', 'rco-preorder__ticker-group');
      for (var j = 0; j < 6; j++) group.appendChild(el('span', 'rco-preorder__ticker-item', text));
      if (i === 1) group.setAttribute('aria-hidden', 'true');
      track.appendChild(group);
    }
    var wrap = el('div', 'rco-preorder__ticker');
    wrap.appendChild(track);
    return wrap;
  }

  /* ---------- Backorder card (product page and pop-up) ---------- */

  function detailsCard(status, options) {
    var headline = options.headline || status.headline || 'This item is on backorder';
    var message = options.message || status.message || '';

    var card = el('div', 'rco-preorder__card');
    // The moving banner is the card's top edge, so the two read as one piece.
    if (options.ticker) card.appendChild(ticker(options.ticker));

    var header = el('div', 'rco-preorder__header');
    var badge = el('span', 'rco-preorder__icon');
    badge.appendChild(icon(TRUCK));
    header.appendChild(badge);
    var heading = el('p', 'rco-preorder__headline', headline);
    if (options.headlineId) heading.id = options.headlineId;
    header.appendChild(heading);
    card.appendChild(header);
    if (message) card.appendChild(el('p', 'rco-preorder__message', message));
    card.appendChild(stepsList(status));

    var note = el('p', 'rco-preorder__note');
    note.appendChild(icon(INFO));
    var noteText = el('span');
    noteText.appendChild(el('strong', '', 'Pre-ordering puts your whole order on hold. '));
    noteText.appendChild(document.createTextNode('Everything in your order, including in-stock items, ships together once the supplies arrive.'));
    note.appendChild(noteText);
    card.appendChild(note);

    var tip = el('p', 'rco-preorder__tip');
    tip.appendChild(el('strong', '', 'Want your in-stock items right away? '));
    tip.appendChild(document.createTextNode('Place two separate orders: one with in-stock items only, and one with the out-of-stock items.'));
    card.appendChild(tip);
    return card;
  }

  /* ---------- Section ---------- */

  function renderSection(section, variantId) {
    var status = preorderFor(variantId);
    var body = section.querySelector('[data-rco-preorder-body]');
    if (!body) return;
    if (!status) {
      section.hidden = true;
      body.innerHTML = '';
      return;
    }

    body.innerHTML = '';
    body.appendChild(detailsCard(status, {
      headline: section.getAttribute('data-headline'),
      message: section.getAttribute('data-message'),
      ticker: section.getAttribute('data-show-marquee') !== 'false' ? section.getAttribute('data-ticker') || backorderLine(status) : null,
    }));
    section.hidden = false;
  }

  // During a pre-order Shopify has quantity, so the theme's stock pill would say
  // "In stock". Swap it for the backorder pill (and put it back afterwards).
  function renderInventoryPill(variantId) {
    var status = preorderFor(variantId);
    document.querySelectorAll('.product__inventory').forEach(function (inventory) {
      var ours = inventory.querySelector(':scope > .rco-preorder-inventory');
      Array.prototype.forEach.call(inventory.children, function (child) {
        if (child !== ours) child.style.display = status ? 'none' : '';
      });
      if (!status) {
        if (ours) ours.remove();
        return;
      }
      if (!ours) {
        ours = el('div', 'rco-preorder-inventory');
        inventory.appendChild(ours);
      }
      ours.innerHTML = '';
      ours.appendChild(statusPill(status));
    });
  }

  /* ---------- Product form ---------- */

  function formVariantId(form) {
    var input = form.querySelector('[name="id"]');
    return input ? input.value : '';
  }

  function submitButtons(form) {
    var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"][name="add"]'));
    if (form.id) {
      buttons = buttons.concat(Array.prototype.slice.call(
        document.querySelectorAll('[type="submit"][name="add"][form="' + form.id + '"]'),
      ));
    }
    return buttons;
  }

  function buttonLabel(button) {
    return button.querySelector('.btn-text > span') || button.querySelector('span:not(.btn-fill)') || button;
  }

  function applyToForm(form, variantId) {
    var id = variantId || formVariantId(form);
    var status = preorderFor(id);
    var hidden = form.querySelector('input[data-rco-preorder-property]');

    if (status) {
      if (!hidden) {
        hidden = el('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + PROPERTY + ']';
        hidden.setAttribute('data-rco-preorder-property', '');
        form.appendChild(hidden);
      }
      hidden.value = shipText(status);
    } else if (hidden) {
      hidden.remove();
    }

    submitButtons(form).forEach(function (button) {
      var label = buttonLabel(button);
      if (status && !button.disabled) {
        if (!button.hasAttribute('data-rco-original-label')) {
          button.setAttribute('data-rco-original-label', label.textContent.trim());
        }
        label.textContent = 'Pre-order';
        button.classList.add('rco-preorder-button');
      } else if (button.hasAttribute('data-rco-original-label')) {
        if (label.textContent.trim() === 'Pre-order') label.textContent = button.getAttribute('data-rco-original-label');
        button.removeAttribute('data-rco-original-label');
        button.classList.remove('rco-preorder-button');
      }
    });
  }

  // variantId/sourceForm come from a variant:change event; otherwise read each form.
  function refreshPage(variantId, sourceForm) {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(function (form) {
      applyToForm(form, form === sourceForm ? variantId : null);
    });
    document.querySelectorAll('[data-rco-preorder-section]').forEach(function (section) {
      if (variantId) section.setAttribute('data-variant-id', variantId);
      renderSection(section, section.getAttribute('data-variant-id') || (forms[0] ? formVariantId(forms[0]) : ''));
    });
    var mainForm = sourceForm || forms[0];
    renderInventoryPill(variantId || (mainForm ? formVariantId(mainForm) : ''));
  }

  /* ---------- Add-to-cart popup ---------- */

  function closeModal() {
    var modal = document.querySelector('.rco-preorder-modal');
    if (modal) modal.remove();
    document.removeEventListener('keydown', onModalKey);
  }

  function onModalKey(event) { if (event.key === 'Escape') closeModal(); }

  // The pre-order agreement pop-up. With onConfirm it asks before adding to the
  // cart; without it, it just explains (for adds that skipped the question).
  function showModal(status, options) {
    closeModal();
    var previousFocus = document.activeElement;
    var modal = el('div', 'rco-preorder-modal');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'RcoPreorderModalTitle');

    var panel = detailsCard(status, {
      ticker: backorderLine(status),
      headline: options.title ? options.title + ' is on backorder' : null,
      headlineId: 'RcoPreorderModalTitle',
    });
    panel.classList.add('rco-preorder-modal__panel');

    var actions = el('div', 'rco-preorder-modal__actions');
    var primary = el('button', 'rco-preorder-modal__button button button--primary');
    primary.type = 'button';
    primary.appendChild(el('span', 'btn-text', options.onConfirm ? 'Agree and add to cart' : 'Got it'));
    actions.appendChild(primary);

    function dismiss() {
      closeModal();
      if (previousFocus && previousFocus.focus) previousFocus.focus();
    }

    if (options.onConfirm) {
      var cancel = el('button', 'rco-preorder-modal__cancel', 'Never mind');
      cancel.type = 'button';
      cancel.addEventListener('click', dismiss);
      actions.appendChild(cancel);
      primary.addEventListener('click', function () {
        closeModal();
        options.onConfirm();
      });
    } else {
      primary.addEventListener('click', dismiss);
    }
    panel.appendChild(actions);
    modal.appendChild(panel);
    modal.addEventListener('click', function (event) { if (event.target === modal) dismiss(); });

    document.body.appendChild(modal);
    document.addEventListener('keydown', onModalKey);
    primary.focus();
  }

  /* ---------- Ask before a pre-order goes in the cart ---------- */

  var agreedForm = null;
  var agreedAt = 0;

  function productTitle(form) {
    var scope = form.closest('product-info, .product__info, [data-product-id], .product-card, .card') || document;
    var heading = scope.querySelector('h1, .product-title, .card__title, h2, h3');
    return heading ? heading.textContent.trim() : '';
  }

  // Runs in the capture phase, before the theme's own add-to-cart handler.
  function onSubmitCapture(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (agreedForm === form) {
      agreedForm = null;
      return;
    }
    var status = preorderFor(formVariantId(form));
    if (!status) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    var submitter = event.submitter;
    showModal(status, {
      title: productTitle(form),
      onConfirm: function () {
        agreedForm = form;
        agreedAt = Date.now();
        if (form.requestSubmit) form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
        else form.submit();
      },
    });
  }

  function variantIdsFromAddBody(body) {
    var ids = [];
    try {
      if (body instanceof FormData) {
        if (body.get('id')) ids.push(body.get('id'));
      } else if (typeof body === 'string') {
        var parsed = JSON.parse(body);
        if (parsed.id) ids.push(parsed.id);
        (parsed.items || []).forEach(function (item) { ids.push(item.id); });
      }
    } catch (e) { /* not JSON */ }
    return ids.map(String);
  }

  function watchCartAdds() {
    if (!window.fetch) return;
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var promise = originalFetch.apply(this, arguments);
      if (url.indexOf('/cart/add') === -1) return promise;

      var ids = variantIdsFromAddBody(init && init.body);
      promise.then(function (res) {
        if (!res.ok) return;
        // Clone now, before the theme reads the body.
        var added = res.clone().json().catch(function () { return {}; });
        Promise.all([loadAll(), added]).then(function (results) {
          var body = results[1] || {};
          var items = (body.items || [body]).filter(function (item) { return item && item.variant_id; });
          items.forEach(function (item) { ids.push(String(item.variant_id)); });
          var title = '';
          var match = null;
          ids.some(function (id) {
            match = preorderFor(id);
            if (match) {
              var item = items.filter(function (i) { return String(i.variant_id) === id; })[0];
              title = item ? item.product_title : '';
            }
            return Boolean(match);
          });
          if (match && Date.now() - agreedAt > 15000) showModal(match, { title: title });
        });
        setTimeout(refreshCartBanners, 600);
      }).catch(function () {});
      return promise;
    };
  }

  /* ---------- Cart banner ---------- */

  var cartHasPreorder = null;

  function refreshCartBanners() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var status = null;
        (cart.items || []).some(function (item) {
          status = preorderFor(item.variant_id);
          if (!status && item.properties && item.properties[PROPERTY]) status = { ship_label: String(item.properties[PROPERTY]).replace(/^(Estimated to ship|Ships)\s+/i, '') };
          return Boolean(status);
        });
        cartHasPreorder = status;
        placeCartBanners();
      })
      .catch(function () {});
  }

  function placeCartBanners() {
    var containers = document.querySelectorAll('cart-drawer cart-items, #main-cart-items, .cart__form cart-items');
    containers.forEach(function (container) {
      var existing = container.parentNode.querySelector(':scope > .rco-preorder-cart-banner');
      if (!cartHasPreorder) {
        if (existing) existing.remove();
        return;
      }
      if (existing) return;
      var banner = el('div', 'rco-preorder-cart-banner');
      banner.setAttribute('role', 'status');
      banner.appendChild(dot());
      var text = el('div');
      text.appendChild(el('strong', '', cartHasPreorder.hardware_type ? backorderLine(cartHasPreorder) : 'Your cart has a pre-order item'));
      text.appendChild(el('span', '', cartHasPreorder.ship_label
        ? 'Your whole order ships together once it is back in stock.'
        : 'Your whole order ships together once it is back in stock.'));
      banner.appendChild(text);
      container.parentNode.insertBefore(banner, container);
    });
  }

  /* ---------- Boot ---------- */

  // Badge reel product pages get the card without adding the section by hand.
  function ensureAutoCard() {
    if (!config.autoCard || document.querySelector('[data-rco-preorder-section]')) return;
    var form = document.querySelector('product-info form[action*="/cart/add"], form[action*="/cart/add"]');
    var section = el('div', 'rco-preorder');
    section.hidden = true;
    section.setAttribute('data-rco-preorder-section', '');
    section.setAttribute('data-placement', 'buy_buttons');
    if (form) section.setAttribute('data-variant-id', formVariantId(form));
    var body = el('div');
    body.setAttribute('data-rco-preorder-body', '');
    body.setAttribute('aria-live', 'polite');
    section.appendChild(body);
    (document.querySelector('main') || document.body).appendChild(section);
  }

  // Sections render below the product; move ours up under the buy buttons.
  function placeSections() {
    document.querySelectorAll('[data-rco-preorder-section][data-placement="buy_buttons"]').forEach(function (section) {
      var target = document.querySelector('product-info .product-form-wrapper, .product__info .product-form-wrapper, .product-form-wrapper');
      if (target && target.nextElementSibling !== section) target.parentNode.insertBefore(section, target.nextSibling);
    });
  }

  function boot() {
    ensureAutoCard();
    placeSections();
    document.addEventListener('submit', onSubmitCapture, true);
    loadAll().then(function () {
      refreshPage(null, null);
      refreshCartBanners();
    });

    document.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      var form = event.target && event.target.matches && event.target.matches('form') ? event.target : null;
      // The theme rewrites the button label in the same tick; apply after it.
      setTimeout(function () { refreshPage(variant ? String(variant.id) : null, form); }, 60);
    }, true);
    document.addEventListener('change', function (event) {
      if (event.target && event.target.closest && event.target.closest('form[action*="/cart/add"], variant-picker, variant-selects')) {
        setTimeout(function () { refreshPage(null, null); }, 120);
      }
    });
    document.addEventListener('cart:refresh', function () { setTimeout(refreshCartBanners, 300); });

    // Cart drawer and cart page re-render their markup; keep the banner in place.
    var pending = false;
    new MutationObserver(function () {
      if (pending || cartHasPreorder === null) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; placeCartBanners(); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  watchCartAdds();
  window.RcoPreorder = { refresh: function () { ready = null; return loadAll().then(function () { refreshPage(null, null); refreshCartBanners(); }); } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
